/**
 * Langue à l'arrivée sur donoor.org (navigateur → /de, /es, /it, /nl, /en),
 * CÔTÉ CLIENT.
 *
 * Règle (décision Karim 2026-10-10) :
 *   1. Un choix explicite (sélecteur de langue, cookie `donoor_langue`) prime.
 *   2. Sinon, un navigateur réglé sur une des 5 langues cibles est envoyé
 *      vers la version correspondante de la page, si elle existe (liste
 *      blanche i18n/pagesTraduites.ts).
 *   3. Sinon, rien ne bouge (français).
 *
 * Pourquoi pas côté serveur : les pages publiques sont mises en cache par le
 * CDN (s-maxage) ; une redirection calculée sur l'en-tête Accept-Language
 * serait servie à TOUS les visiteurs suivants.
 *
 * Les robots (Googlebot rend le JavaScript avec un navigateur en anglais) ne
 * sont jamais redirigés : chaque adresse doit rester indexée dans SA langue.
 */
import { COOKIE_LANGUE, LANGUES, langueDuChemin, type Langue } from "~/utils/langue";

const ROBOT = /bot|crawl|spider|slurp|lighthouse|headless|preview|facebookexternalhit|embedly|whatsapp|telegram|inspectiontool|googleother/i;

function langueNavigateur(): Langue {
  const langues = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const l of langues) {
    const code = (l ?? "").toLowerCase().split("-")[0];
    const trouvee = LANGUES.find((candidate) => candidate === code);
    if (trouvee) return trouvee;
  }
  return "fr";
}

export default defineNuxtPlugin((nuxtApp) => {
  // `navigator.webdriver` : navigateur piloté par un programme. PageSpeed
  // Insights se présente comme un Chrome Android ordinaire (aucun « bot » ni
  // « Lighthouse » dans son nom depuis 2026) : il était renvoyé vers /en et
  // mesurait la page anglaise au lieu de la page demandée (audit SEO 18/10).
  if (ROBOT.test(navigator.userAgent) || navigator.webdriver) return;

  const choix = useCookie<Langue | null>(COOKIE_LANGUE).value;
  const voulue: Langue = choix && LANGUES.includes(choix) ? choix : langueNavigateur();

  const router = useRouter();
  const switchLocalePath = useSwitchLocalePath();

  nuxtApp.hook("app:mounted", () => {
    const route = router.currentRoute.value;
    if (langueDuChemin(route.path) === voulue) return;
    const cible = switchLocalePath(voulue);
    // Chaîne vide = la page n'existe pas dans cette langue : on reste.
    if (!cible || cible === route.fullPath) return;
    // VRAI rechargement, pas une navigation interne : les textes écrits par
    // l'association (traduits par l'API selon la langue de la page) étaient
    // déjà chargés en français et restaient en français après la bascule —
    // le serveur rend directement la page dans la bonne langue (constaté en
    // prod le 14/10/2026 sur /b/chit-chat-spooky).
    window.location.replace(cible);
  });
});
